import React, { type ReactElement, useMemo } from 'react';
import clsx from 'clsx';
import './authn-layout.less';
import { EventProvider, useEventListener } from '@imprivata-cloud/core';
import {
  NotificationSystem,
  notificationFromAppEvent,
  useNotificationChannel,
} from '@imprivata-cloud/design-system';
import type { BannerProps } from '../Banner/banner';
import ImprivataLogo from '../../../images/Imprivata_Logo.svg?react';
import { AuthenticationHeader } from './components/authentication-header/authentication-header';

export enum LayoutHeaders {
  AUTHENTICATION = 'AUTHENTICATION',
  NONE = 'NONE',
}

interface AuthnLayoutProps {
  children: React.ReactNode;
  logoUrl?: string | React.ReactNode;
  hexImage?: React.ReactNode;
  header: LayoutHeaders;
  banner?: BannerProps;
  renderCustomBanner?: ReactElement;
  hideLayout?: boolean;
}

// Must render inside EventProvider: useEventListener reads its context
const CoreNotificationSystem = () => {
  const channel = useNotificationChannel();
  useEventListener(event => {
    channel.publish(notificationFromAppEvent(event));
  });
  return <NotificationSystem subscribe={channel.subscribe} />;
};

export const AuthnLayout = ({
  children,
  logoUrl,
  hexImage,
  header,
  banner,
  renderCustomBanner,
  hideLayout,
}: AuthnLayoutProps) => {
  const loadLogo = useMemo(
    () =>
      typeof logoUrl === 'string' ? (
        <img src={logoUrl} alt="logo" className="card-header-logo" />
      ) : (
        logoUrl
      ),
    [logoUrl],
  );

  const renderHeader = useMemo(() => {
    switch (header) {
      case LayoutHeaders.AUTHENTICATION:
        return (
          <AuthenticationHeader
            banner={!renderCustomBanner ? banner : undefined}
            logoUrl={loadLogo}
            hexImage={hexImage}
          />
        );
      case LayoutHeaders.NONE:
        return null;
    }
  }, [header, banner, loadLogo, hexImage, renderCustomBanner]);

  // NotificationSystem and Authn both use core event hooks, which need the provider
  return (
    <EventProvider>
      <div className={clsx('authn-layout-root', hideLayout && 'hide-layout')}>
        <div className="authn-layout-notification-system">
          <CoreNotificationSystem />
        </div>
        {renderCustomBanner}
        {renderHeader}
        <div className="authn-layout-body">{children}</div>
        <div className="authn-layout-footer">
          <ImprivataLogo />
        </div>
      </div>
    </EventProvider>
  );
};
